/**
 * Stationing along a project alignment (spec §17 rule 4): the web mirror of the API's pure
 * `src/helpers/alignment-stationing.js` (api-drawings), held to the same shared fixtures
 * (test/fixtures/alignment-stationing.json, copied into alignment-stationing.fixtures.ts).
 *
 * An alignment is a WGS84 polyline (`coords`, [lon, lat]) with stations in feet: `stations` (one per vertex) and/or
 * `start`. Its stationing is one kind for the whole line (R44b/R44c):
 *  - 'plan': every vertex has a station, they strictly increase, and every segment's span is at least its chord less
 *    max(0.2% of it, 0.5 ft). A point between two vertices takes its geodesic share of that span. Stations that fall
 *    along the line are the same alignment drawn the other way; it is walked in station order. A repeated vertex (a
 *    zero-length segment) is ignored.
 *  - 'chainage': anything else. Station = the first station (else `start`, else 0) + geodesic chainage.
 *
 * Distances: each segment in a local east/north plane at its mid-latitude, scaled by the WGS84 meridional (M) and
 * prime-vertical (N) radii. Feet are international feet (0.3048 m). Offsets are perpendicular; R is right of the
 * stationing direction. The position this works on never leaves the browser; only a station is sent (spec §16 rule 7).
 */

export type Stationing = 'plan' | 'chainage';

export interface Alignment {
  /** [lon, lat] */
  coords: number[][];
  /** Feet per vertex, or null */
  stations?: number[] | null;
  /** Feet; used when there are no per-vertex stations */
  start?: number;
}

export interface StationAt {
  station: number;
  offset_ft: number;
  side: 'L' | 'R' | null;
  beyond: 'start' | 'end' | null;
  stationing: Stationing;
}

/** One alignment of the project, from `project.alignment_geometry` */
export interface ProjectAlignment {
  name: string;
  alignment: Alignment;
}

const A = 6378137;
const F = 1 / 298.257223563;
const E2 = F * (2 - F);
export const FT = 0.3048;
const RAD = Math.PI / 180;
export const PLAN_TOLERANCE = 0.002;
export const PLAN_FLOOR_FT = 0.5;

function radii(lat: number): { north: number; east: number } {
  const s = Math.sin(lat * RAD);
  const w = Math.sqrt(1 - E2 * s * s);
  return { north: (A * (1 - E2)) / (w * w * w), east: (A / w) * Math.cos(lat * RAD) };
}

function frame([lon0, lat0]: number[], latRef = lat0) {
  const r = radii(latRef);
  return {
    toLocal: ([lon, lat]: number[]) => [(lon - lon0) * RAD * r.east, (lat - lat0) * RAD * r.north],
    toLonLat: ([x, y]: number[]) => [lon0 + x / (r.east * RAD), lat0 + y / (r.north * RAD)],
  };
}

function segmentFrame(p: number[], q: number[]) {
  return frame(p, (p[1] + q[1]) / 2);
}

function segmentMetres(p: number[], q: number[]): number {
  const [x, y] = segmentFrame(p, q).toLocal(q);
  return Math.hypot(x, y);
}

/** A segment shorter than this (feet) is a repeated vertex */
const ZERO_FT = 0.01;

/** Plan stationing (R44b/R44c): a zero-length segment (a repeated vertex) only needs its stations not to go backwards */
function isPlan(stations: number[] | null | undefined, lengths: number[]): boolean {
  if (!Array.isArray(stations) || stations.length !== lengths.length + 1) {
    return false;
  }
  if (!stations.every(s => typeof s === 'number' && Number.isFinite(s))) {
    return false;
  }
  if (!(stations[stations.length - 1] > stations[0])) {
    return false;
  }
  return lengths.every((m, i) => {
    const span = stations[i + 1] - stations[i];
    const chord = m / FT;
    if (chord < ZERO_FT) {
      return span >= 0;
    }
    return span > 0 && span >= chord - Math.max(chord * PLAN_TOLERANCE, PLAN_FLOOR_FT);
  });
}

function prepare({ coords, stations = null, start = 0 }: Alignment): { coords: number[][]; at: number[]; lengths: number[]; stationing: Stationing } {
  if (!Array.isArray(coords) || coords.length < 2) {
    throw new Error('An alignment needs at least two points');
  }
  const lengths: number[] = [];
  for (let i = 1; i < coords.length; i += 1) {
    lengths.push(segmentMetres(coords[i - 1], coords[i]));
  }
  if (isPlan(stations, lengths)) {
    return { coords, at: [...stations], lengths, stationing: 'plan' };
  }
  // Drawn against the stationing (stations strictly falling along the line): the same alignment walked the other way,
  // so stations rise and right is right of the stationing direction
  if (Array.isArray(stations) && isPlan([...stations].reverse(), [...lengths].reverse())) {
    return { coords: [...coords].reverse(), at: [...stations].reverse(), lengths: [...lengths].reverse(), stationing: 'plan' };
  }
  const first = Array.isArray(stations) && Number.isFinite(stations[0]) ? stations[0] : Number(start) || 0;
  const at = [first];
  lengths.forEach((m, i) => at.push(at[i] + m / FT));
  return { coords, at, lengths, stationing: 'chainage' };
}

export function stationingOf(alignment: Alignment): Stationing {
  return prepare(alignment).stationing;
}

/** The first and last station, feet */
export function stationRange(alignment: Alignment): { first: number; last: number } {
  const { at } = prepare(alignment);
  return { first: at[0], last: at[at.length - 1] };
}

/** Total geodesic length, feet */
export function chainage(alignment: Alignment): number {
  return prepare(alignment).lengths.reduce((a, b) => a + b, 0) / FT;
}

/**
 * The station of a WGS84 point: the nearest point on the alignment, its station, and the perpendicular offset (feet)
 * and side. Beyond either end it is measured from that end vertex (`beyond`), its offset the straight distance.
 */
export function stationAt(alignment: Alignment, { lat, lon }: { lat: number; lon: number }): StationAt {
  const { coords, at, stationing } = prepare(alignment);
  let best: { dist: number; station: number; side: 'L' | 'R' | null; beyond: 'start' | 'end' | null } | null = null;
  for (let i = 1; i < coords.length; i += 1) {
    const f = segmentFrame(coords[i - 1], coords[i]);
    const [dx, dy] = f.toLocal(coords[i]);
    const [px, py] = f.toLocal([lon, lat]);
    const len2 = dx * dx + dy * dy;
    const raw = len2 > 0 ? (px * dx + py * dy) / len2 : 0;
    const t = Math.min(1, Math.max(0, raw));
    const ox = px - t * dx;
    const oy = py - t * dy;
    const dist = Math.hypot(ox, oy);
    if (!best || dist < best.dist - 1e-9) {
      const cross = dx * py - dy * px;
      let beyond: 'start' | 'end' | null = null;
      if (raw < 0 && i === 1) {
        beyond = 'start';
      }
      if (raw > 1 && i === coords.length - 1) {
        beyond = 'end';
      }
      let side: 'L' | 'R' | null = null;
      if (Math.abs(cross) / Math.sqrt(len2 || 1) >= 1e-6) {
        side = cross > 0 ? 'L' : 'R';
      }
      best = { dist, station: at[i - 1] + t * (at[i] - at[i - 1]), side, beyond };
    }
  }
  return { station: best.station, offset_ft: best.dist / FT, side: best.side, beyond: best.beyond, stationing };
}

/** The WGS84 point at a station, `offsetFt` to the `side` of the line; null outside the alignment */
export function pointAt(alignment: Alignment, station: number, offsetFt = 0, side: 'L' | 'R' = 'R'): { lat: number; lon: number } | null {
  const { coords, at } = prepare(alignment);
  for (let i = 1; i < coords.length; i += 1) {
    if (station >= at[i - 1] - 1e-9 && station <= at[i] + 1e-9) {
      const span = at[i] - at[i - 1];
      const t = span === 0 ? 0 : (station - at[i - 1]) / span;
      const f = segmentFrame(coords[i - 1], coords[i]);
      const [dx, dy] = f.toLocal(coords[i]);
      const len = Math.hypot(dx, dy);
      const off = (Number(offsetFt) || 0) * FT * (side === 'L' ? 1 : -1);
      const x = t * dx + (len > 0 ? (-dy / len) * off : 0);
      const y = t * dy + (len > 0 ? (dx / len) * off : 0);
      const [lon, lat] = f.toLonLat([x, y]);
      return { lat, lon };
    }
  }
  return null;
}

/** Feet from a station: a number, or a name like "1018+50" / "1018+50.25" (the API's StationModel rule); NaN otherwise */
export function stationFeetOf(value: unknown): number {
  if (typeof value === 'number') {
    return value;
  }
  const m = /^\s*(\d+)\+(\d+)(\.\d+)?\s*$/.exec(String(value ?? ''));
  return m ? parseInt(m[1], 10) * 100 + parseInt(m[2], 10) + Number(m[3] || 0) : Number.NaN;
}

/** "1018+50", "1018+50.3" (a tenth when it has one) */
export function stationName(feet: number): string {
  const tenths = Math.round(feet * 10) / 10;
  const hundreds = Math.floor(tenths / 100);
  const rest = tenths - hundreds * 100;
  const whole = Math.floor(rest + 1e-9);
  const frac = Math.round((rest - whole) * 10);
  return `${hundreds}+${String(whole).padStart(2, '0')}${frac ? `.${frac}` : ''}`;
}

/**
 * Every LineString of `project.alignment_geometry` (a FeatureCollection, or its JSON) as an alignment, mirroring the
 * API's projectAlignment: per-vertex stations only when every vertex has a readable one; otherwise the first name as
 * the start.
 */
export function projectAlignments(geometry: unknown): ProjectAlignment[] {
  let fc: { features?: unknown[] } | null = null;
  try {
    fc = (typeof geometry === 'string' ? JSON.parse(geometry) : geometry) as { features?: unknown[] } | null;
  } catch {
    return [];
  }
  const features = (Array.isArray(fc?.features) ? fc.features : []) as {
    geometry?: { type?: string; coordinates?: number[][] };
    properties?: { name?: string; stations?: unknown[] };
  }[];
  return features
    .filter(f => f?.geometry?.type === 'LineString' && Array.isArray(f.geometry.coordinates) && f.geometry.coordinates.length >= 2)
    .map(f => {
      const coords = f.geometry.coordinates.map(([lon, lat]) => [lon, lat]);
      const names = Array.isArray(f.properties?.stations) ? f.properties.stations : [];
      const feet = names.map(s => stationFeetOf((s as { name?: unknown })?.name ?? s));
      const complete = feet.length === coords.length && feet.every(Number.isFinite);
      return {
        name: f.properties?.name || '',
        alignment: { coords, stations: complete ? feet : null, start: Number.isFinite(feet[0]) ? feet[0] : 0 },
      };
    });
}
